import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { toast } from '@/components/toast';
import CloudUpload from '@/images/CloudUpload.svg';
import { UploadingMediaEntity } from '@/pages/AdvancedEditor/components/panels/media/types';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { logger } from '@/services/logger';
import theme from '@/theme';
import { annoynmousUserToEditorPagePostSsoReturnTo } from '@/utils/utils';
import styled from '@emotion/styled';
import { Card } from '@mui/material';
import { nanoid } from 'nanoid';
import { FileError, FileRejection, useDropzone } from 'react-dropzone';
import { getImageMetadata, ImageData } from './getImageMetadata';
import { getVideoMetadata, VideoData } from './getVideoMetadata';

const MAX_VIDEO_SIZE = 1 * 1024 * 1024 * 1024; // 1 GB in bytes
const MAX_GIF_SIZE = 200 * 1024 * 1024; // 200 MB in bytes
const MAX_IMAGE_SIZE = 200 * 1024 * 1024; // 200 MB in bytes

const UploadBox = styled(Card, { shouldForwardProp: prop => prop !== 'isDragActive' })<{ isDragActive?: boolean }>`
    display: flex;
    width: 100%;
    height: 150px;
    border-radius: 12px;
    cursor: pointer;
    align-items: center;
    justify-content: center;

    border: 1px solid ${({ theme, isDragActive }) => (isDragActive ? theme.colors.primary[900] : theme.colors.black[300])};

    ${({ isDragActive, theme }) =>
        isDragActive
            ? `
            background-color: ${theme.colors.primary.bgLight} !important;
        `
            : `
            border: 0;
            background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='12' ry='12' stroke='%23DBDBDBFF' stroke-width='3' stroke-dasharray='6%2c 14' stroke-dashoffset='7' stroke-linecap='square'/%3e%3c/svg%3e");    
        `}

    box-shadow:
        0.3px 0.8px 1.1px 0px rgba(187, 187, 187, 0.11),
        0.5px 1.3px 1.8px -0.6px rgba(187, 187, 187, 0.18),
        1.1px 2.7px 3.8px -1.2px rgba(187, 187, 187, 0.26);
`;

const Dropzone = styled(UploadBox)<{ isDragActive?: boolean }>`
    cursor: pointer;
    outline: none !important;
    transition: background linear 0.2s;
    border: none;

    :hover {
        background-color: ${({ theme }) => theme.colors.black[100]};
    }
`;

const UploadBoxContent = styled.div`
    display: flex;
    gap: 4px;
    align-items: center;
    padding: 32px 20px;
    flex-direction: column;
`;

const MainText = styled.span`
    color: ${({ theme }) => theme.colors.black.main};
    text-align: center;

    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.32px;
    margin-bottom: 4px;
`;

const SubText = styled.span`
    color: ${({ theme }) => theme.colors.black[600]};
    align-items: center;

    font-size: 14px;
    font-weight: 400;
    letter-spacing: 0.17px;
`;

const DropTextContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

export enum MEDIA_ERROR_CODES {
    VIDEO_FILE_TOO_LARGE = 'video-file-too-large',
    GIF_FILE_TOO_LARGE = 'gif-file-too-large',
    IMAGE_FILE_TOO_LARGE = 'image-file-too-large',
    FILE_INVALID_TYPE = 'file-invalid-type',
    UPLOAD_FAILED = 'upload-failed',
    UNSUPPORTED_TYPE = 'unsupported-type',
    TOO_MANY_FILES = 'too-many-files',
}

const errorMessage: Record<MEDIA_ERROR_CODES, string> = {
    [MEDIA_ERROR_CODES.VIDEO_FILE_TOO_LARGE]: 'Please upload videos up to 1 GB',
    [MEDIA_ERROR_CODES.GIF_FILE_TOO_LARGE]: 'Please upload GIFs up to 200 MB',
    [MEDIA_ERROR_CODES.IMAGE_FILE_TOO_LARGE]: 'Please upload images up to 200 MB',
    [MEDIA_ERROR_CODES.FILE_INVALID_TYPE]: 'File type not supported',
    [MEDIA_ERROR_CODES.UPLOAD_FAILED]: 'Media failed to upload, please try again',
    [MEDIA_ERROR_CODES.UNSUPPORTED_TYPE]: 'Media format not supported by the browser',
    [MEDIA_ERROR_CODES.TOO_MANY_FILES]: 'Upload one file at a time, please',
};

const getMediaType = (file: File): string | null => {
    if (file.type.includes('video')) {
        return 'video';
    }

    if (file.type.includes('image/gif')) {
        return 'gif';
    }

    if (file.type.includes('image')) {
        return 'image';
    }

    return null;
};

const getFileValidator = (file: File): FileError | null => {
    if (file.type.includes('video') && file.size > MAX_VIDEO_SIZE) {
        return { code: MEDIA_ERROR_CODES.VIDEO_FILE_TOO_LARGE, message: '' };
    }
    if (file.type === 'image/gif' && file.size > MAX_GIF_SIZE) {
        return { code: MEDIA_ERROR_CODES.GIF_FILE_TOO_LARGE, message: '' };
    }
    if (file.type.includes('image') && file.size > MAX_IMAGE_SIZE) {
        return { code: MEDIA_ERROR_CODES.IMAGE_FILE_TOO_LARGE, message: '' };
    }

    return null;
};

const validateFile = (file: File): FileError[] | null => {
    const error = getFileValidator(file);
    return error ? [error] : null;
};

export const getFileMetadata = async (file: File): Promise<ImageData | VideoData | null> => {
    if (file.type.includes('image')) {
        return getImageMetadata(file);
    }
    if (file.type.includes('video')) {
        return getVideoMetadata(file);
    }
    return null;
};

interface Props {
    onInputChange: (media: UploadingMediaEntity) => Promise<void>;
}

export const MediaUpload = ({ onInputChange }: Props) => {
    const { isAnonymous, loginWithRedirect } = useAuth();
    const mixpanel = useAnalytics();

    const onDrop = async (acceptedFiles: File[]) => {
        mixpanel?.track('video-upload-media', { event: 'input' });

        if (!acceptedFiles.length) {
            return;
        }

        const file = acceptedFiles[0];

        mixpanel?.track('video-upload-media', {
            event: 'start',
            mediaType: getMediaType(file),
            fileSize: file.size,
            fileType: file.type,
        });

        const id = nanoid();

        try {
            const metadata = await getFileMetadata(file);
            if (metadata) {
                await onInputChange({ id, ...metadata, isLoading: true, file, createdAt: new Date().toISOString() });
                mixpanel?.track('video-upload-media', { event: 'success', mediaType: getMediaType(file) });
            }
        } catch (e: any) {
            if (e?.code === MEDIA_ERROR_CODES.UNSUPPORTED_TYPE) {
                toast.error({
                    title: `Error uploading ${file.name}`,
                    message: errorMessage[MEDIA_ERROR_CODES.UNSUPPORTED_TYPE],
                    toastId: MEDIA_ERROR_CODES.UNSUPPORTED_TYPE,
                });
                logger.error(errorMessage[MEDIA_ERROR_CODES.UNSUPPORTED_TYPE], { e });
                return;
            }

            toast.error({ title: errorMessage[MEDIA_ERROR_CODES.UPLOAD_FAILED], toastId: MEDIA_ERROR_CODES.UPLOAD_FAILED });
            logger.error('Error while uploading media', { e });
        }
    };

    const onDropRejected = (fileRejections: FileRejection[]) => {
        fileRejections.forEach(({ errors }) => {
            errors.forEach(error => {
                const toastMethod = error.code === MEDIA_ERROR_CODES.UPLOAD_FAILED ? toast.error : toast.info;
                toastMethod({ title: errorMessage[error.code as MEDIA_ERROR_CODES], toastId: error.code });
            });
        });
    };

    const handleAnonymous = async () => {
        mixpanel?.track('go-to-signup', { event: 'click', from: 'upload-media-box', isAnonymous });
        await loginWithRedirect({ appState: { returnTo: annoynmousUserToEditorPagePostSsoReturnTo } });
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        onDropRejected,
        accept: {
            'video/mp4': [],
            'video/quicktime': [],
            'image/gif': [],
            'image/jpeg': [],
            'image/png': [],
            'image/svg+xml': [],
        },
        validator: validateFile,
        multiple: false,
    });

    return (
        <UploadBox isDragActive={isDragActive} data-testid="media-upload-box">
            <Dropzone {...getRootProps()} {...(isAnonymous ? { onClick: handleAnonymous } : {})} isDragActive={isDragActive}>
                <input {...getInputProps()} {...(isAnonymous ? { onClick: handleAnonymous } : {})} />
                {isDragActive ? (
                    <DropTextContainer>
                        <MainText>Drag to upload</MainText>
                    </DropTextContainer>
                ) : (
                    <UploadBoxContent>
                        <MaskedIcon src={CloudUpload} size="32px" color={theme.colors.black[700]} />

                        <MainText id="upload-audio">Upload media</MainText>
                        <SubText>Images, videos or GIFs</SubText>
                    </UploadBoxContent>
                )}
            </Dropzone>
        </UploadBox>
    );
};
